import {
  CouponListV2,
  CouponV2,
  CouponsApi,
  ErrorResponse,
  FindCouponsV2Request,
} from '@citruscamps/citrus-client'
import { Dispatch } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query'
import {
  DefaultDetailsPagination,
  DefaultMainPagination,
} from '../../../../../constants/pagination'
import {
  IFetchManyProps,
  NonQueryAttributes,
  useFetchInfiniteMany,
  useFetchMany,
} from '../../../../../hooks/useFetchMany'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'
import { useUpdateCouponV2 } from '../../CouponDetails/hooks/useUpdateCouponV2'

export const DefaultCouponFetchProps: CouponFetchProps = {
  sort: 'updated',
  order: 'DESC',
  query: {},
}

export type CouponFilterProps = Omit<FindCouponsV2Request, NonQueryAttributes>

export interface CouponFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  query: CouponFilterProps
}

type IProps = Pick<
  IFetchManyProps<CouponV2, CouponFilterProps>,
  'programId' | 'fetchProps' | 'pagination' | 'isEnabled' | 'setFetchProps'
>

interface FetchCoupons {
  data: CouponV2[]
  error?: ErrorResponse
  fetchProps: CouponFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  handleEnable: (coupon: CouponV2) => Promise<void>
  handleDelete: (coupon: CouponV2) => Promise<void>
  setFetchProps: (value?: CouponFetchProps) => void
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchCouponsV2 = (props: IProps): FetchCoupons => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setPagination,
    setSearch,
    setFetchProps,
  } = useFetchMany<CouponV2, CouponFilterProps>({
    type: 'coupon',
    programId: props.programId,
    fetchProps: props.fetchProps,
    defaultFetchProps: DefaultCouponFetchProps,
    pagination: props.pagination || DefaultMainPagination,
    isEnabled: props.isEnabled,
    setFetchProps: props.setFetchProps,
    queryFn: async (queryParams): Promise<CouponListV2> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new CouponsApi(generateApiConfig())
      return await client.findCouponsV2(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          id: query.id,
          exclude_id: query.exclude_id,
          is_enabled: query.is_enabled,
          type: query.type && query.type.length > 0 ? query.type : undefined,
          redeemed_lt: query.redeemed_lt ? new Date(query.redeemed_lt) : undefined,
          redeemed_gt: query.redeemed_gt ? new Date(query.redeemed_gt) : undefined,
          redeemed_lte: query.redeemed_lte ? new Date(query.redeemed_lte) : undefined,
          redeemed_gte: query.redeemed_gte ? new Date(query.redeemed_gte) : undefined,
          redeem_by_lt: query.redeem_by_lt ? new Date(query.redeem_by_lt) : undefined,
          redeem_by_gt: query.redeem_by_gt ? new Date(query.redeem_by_gt) : undefined,
          redeem_by_lte: query.redeem_by_lte ? new Date(query.redeem_by_lte) : undefined,
          redeem_by_gte: query.redeem_by_gte ? new Date(query.redeem_by_gte) : undefined,
          membership_packages:
            !!query.membership_packages && query.membership_packages?.length > 0
              ? query.membership_packages
              : undefined,
        },
        { signal },
      )
    },
  })

  const { handleEnable, handleDelete } = useUpdateCouponV2({ programId: props.programId })

  return {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    handleEnable,
    handleDelete,
    setFetchProps,
    setPagination,
    setSearch,
  }
}

interface IInfiniteProps extends Omit<IProps, 'isFilterPersisted'> {
  fetchAll?: boolean
}

interface FetchInfiniteCoupons extends Omit<FetchCoupons, 'setFetchProps' | 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult<CouponListV2, ErrorResponse>>
}

export const useFetchInfiniteCouponsV2 = (props: IInfiniteProps): FetchInfiniteCoupons => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setSearch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useFetchInfiniteMany<CouponV2, CouponFilterProps>({
    type: 'coupon',
    programId: props.programId,
    fetchProps: props.fetchProps || DefaultCouponFetchProps,
    pagination: props.pagination || DefaultDetailsPagination,
    isEnabled: props.isEnabled,
    fetchAll: props.fetchAll,
    queryFn: async (queryParams): Promise<CouponListV2> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new CouponsApi(generateApiConfig())
      return await client.findCouponsV2(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          id: query.id,
          exclude_id: query.exclude_id,
          is_enabled: query.is_enabled,
          type: query.type && query.type.length > 0 ? query.type : undefined,
          redeemed_lt: query.redeemed_lt ? new Date(query.redeemed_lt) : undefined,
          redeemed_gt: query.redeemed_gt ? new Date(query.redeemed_gt) : undefined,
          redeemed_lte: query.redeemed_lte ? new Date(query.redeemed_lte) : undefined,
          redeemed_gte: query.redeemed_gte ? new Date(query.redeemed_gte) : undefined,
          redeem_by_lt: query.redeem_by_lt ? new Date(query.redeem_by_lt) : undefined,
          redeem_by_gt: query.redeem_by_gt ? new Date(query.redeem_by_gt) : undefined,
          redeem_by_lte: query.redeem_by_lte ? new Date(query.redeem_by_lte) : undefined,
          redeem_by_gte: query.redeem_by_gte ? new Date(query.redeem_by_gte) : undefined,
          membership_packages:
            !!query.membership_packages && query.membership_packages?.length > 0
              ? query.membership_packages
              : undefined,
        },
        { signal },
      )
    },
  })

  const { handleEnable, handleDelete } = useUpdateCouponV2({ programId: props.programId })

  return {
    data,
    error,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    pagination,
    search: _search,
    handleEnable,
    handleDelete,
    fetchNextPage,
    setSearch,
  }
}
